import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, SolidButton } from '../buttons-visuals';
import { ContentContainer } from '../containers';
import ConfirmModal from '../confirmModal';
import { Select, TextInput } from '../inputs';
import Modal from '../modal';
import { useLeague, useProfile, useUserPermissionService } from '../../hooks/store';
import { ClassSessionService } from '../../services/classSessionService';

import './index.scss';

interface ClassSessionScheduleProps {
    organizationId?: string;
    showCreate?: boolean;
}

const isUpcoming = (session: Core.Models.ClassSession) => session.currentState !== 'IsComplete';

const ClassSessionSchedule = (props: ClassSessionScheduleProps): JSX.Element => {
    const league = useLeague();
    const profile = useProfile();
    const userPermissionService = useUserPermissionService();
    const organizationId = props.organizationId || profile?.organizationId;

    const [sessions, setSessions] = useState<Core.Models.ClassSession[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [pendingActionId, setPendingActionId] = useState<string | undefined>();
    const [sessionToDelete, setSessionToDelete] = useState<Core.Models.ClassSession | undefined>();

    const canManage = useMemo(() => {
        if (!organizationId) return false;
        return userPermissionService.hasOrganizationAccess(Core.Models.PermissionLevel.ElevatedEdit, organizationId);
    }, [organizationId, userPermissionService]);

    const load = useCallback(async () => {
        if (!organizationId || league?.featureGroup !== Core.Models.FeatureGroup.NCBC) return;
        setIsLoading(true);
        try {
            setSessions(await ClassSessionService.list(organizationId));
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    }, [league?.featureGroup, organizationId]);

    useEffect(() => {
        load();
    }, [load]);

    if (league?.featureGroup !== Core.Models.FeatureGroup.NCBC || !organizationId) return <></>;

    const upcoming = sessions.filter(isUpcoming);
    const past = sessions.filter((s) => !isUpcoming(s));
    const showCreateButton = (props.showCreate ?? true) && canManage;

    const join = async (matchId: string) => {
        setPendingActionId(matchId);
        try {
            await ClassSessionService.join(matchId);
            toast.success('Joined session.');
            await load();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setPendingActionId(undefined);
        }
    };

    const confirmDelete = async () => {
        if (!sessionToDelete) return;
        const matchId = sessionToDelete.matchId;
        setPendingActionId(matchId);
        try {
            await ClassSessionService.delete(matchId);
            toast.success('Session deleted.');
            setSessionToDelete(undefined);
            await load();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setPendingActionId(undefined);
        }
    };

    const renderSessionRow = (session: Core.Models.ClassSession) => (
        <li key={session.matchId} className="class-session-schedule__row">
            <div className="class-session-schedule__row-main">
                <div className="class-session-schedule__row-title">
                    {session.gameName}
                    {session.subject ? ` · ${session.subject}` : ''}
                </div>
                <div className="class-session-schedule__row-topic">{session.topic}</div>
                <div className="class-session-schedule__row-meta">
                    {session.startTimeUtc
                        ? new Date(session.startTimeUtc).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                          })
                        : 'Time TBD'}{' '}
                    · {session.participantCount} joined
                    {!isUpcoming(session) ? ' · Completed' : ''}
                </div>
            </div>
            <div className="class-session-schedule__row-actions">
                <HollowButton as="link" size="small" to={`/matches/${session.matchId}`}>
                    Open session
                </HollowButton>
                {session.launchUrl && session.canManage && (
                    <HollowButton
                        as="a"
                        size="small"
                        href={session.launchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Open in QuizWhizzer
                    </HollowButton>
                )}
                {session.canJoin && !session.hasJoined && (
                    <SolidButton
                        as="button"
                        size="small"
                        pending={pendingActionId === session.matchId}
                        disabled={!!pendingActionId}
                        onClick={() => join(session.matchId)}
                    >
                        Join
                    </SolidButton>
                )}
                {session.hasJoined && <span className="class-session-schedule__joined">Joined</span>}
                {session.canManage && isUpcoming(session) && (
                    <HollowButton
                        as="button"
                        size="small"
                        color="destructive"
                        disabled={!!pendingActionId}
                        onClick={() => setSessionToDelete(session)}
                    >
                        Delete
                    </HollowButton>
                )}
            </div>
        </li>
    );

    const renderSection = (
        title: string,
        items: Core.Models.ClassSession[],
        emptyMessage?: string,
        controls?: React.ReactNode,
        emptyAction?: React.ReactNode
    ) => (
        <ContentContainer
            className="class-session-schedule shadow-medium p0"
            shade={Core.Models.Shades.Dark20}
            radius={Core.Models.RadiusSizes.Large}
        >
            <div className="class-session-schedule__header">
                <h2 className="heading-2 mb0">{title}</h2>
                {controls && <div className="class-session-schedule__controls">{controls}</div>}
            </div>
            <div className="class-session-schedule__body">
                {isLoading && <p className="class-session-schedule__empty">Loading sessions…</p>}
                {!isLoading && items.length === 0 && emptyMessage && (
                    <div className="class-session-schedule__empty-state">
                        <p className="class-session-schedule__empty">{emptyMessage}</p>
                        {emptyAction}
                    </div>
                )}
                {!isLoading && items.length > 0 && (
                    <ul className="class-session-schedule__list list-unstyled">{items.map(renderSessionRow)}</ul>
                )}
            </div>
        </ContentContainer>
    );

    return (
        <div className="class-session-schedule-wrap">
            {renderSection(
                'Upcoming sessions',
                upcoming,
                'No upcoming sessions for your school yet.',
                showCreateButton ? (
                    <SolidButton as="button" size="small" onClick={() => setShowCreateModal(true)}>
                        Create session
                    </SolidButton>
                ) : undefined,
                showCreateButton ? (
                    <SolidButton as="button" size="small" onClick={() => setShowCreateModal(true)}>
                        Create session
                    </SolidButton>
                ) : undefined
            )}

            {past.length > 0 && renderSection('Recent sessions', past)}

            {showCreateModal && organizationId && (
                <CreateSessionModal
                    organizationId={organizationId}
                    onClose={() => setShowCreateModal(false)}
                    onCreated={async () => {
                        setShowCreateModal(false);
                        await load();
                    }}
                />
            )}

            {sessionToDelete && (
                <ConfirmModal
                    onCancel={() => setSessionToDelete(undefined)}
                    onConfirm={confirmDelete}
                    title="Delete session?"
                >
                    <p>
                        Delete{' '}
                        <strong>
                            {sessionToDelete.gameName}
                            {sessionToDelete.topic ? ` — ${sessionToDelete.topic}` : ''}
                        </strong>
                        ? This cannot be undone.
                    </p>
                </ConfirmModal>
            )}
        </div>
    );
};

const CreateSessionModal = ({
    organizationId,
    onClose,
    onCreated,
}: {
    organizationId: string;
    onClose: () => void;
    onCreated: () => Promise<void>;
}) => {
    const [games, setGames] = useState<Core.Models.QuizWhizzerCatalogGame[]>([]);
    const [topics, setTopics] = useState<Core.Models.QuizWhizzerTopic[]>([]);
    const [gameId, setGameId] = useState('');
    const [subject, setSubject] = useState('');
    const [topicId, setTopicId] = useState('');
    const [startLocal, setStartLocal] = useState('');
    const [pending, setPending] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [createdLaunchUrl, setCreatedLaunchUrl] = useState<string | undefined>();

    useEffect(() => {
        (async () => {
            setCatalogLoading(true);
            try {
                const list = await ClassSessionService.getCatalogGames();
                setGames(list);
                if (list.length === 0) {
                    toast.error(
                        'QuizWhizzer catalog is empty. A league host must open Settings → QuizWhizzer Catalog and click Initialize / seed catalog.'
                    );
                    return;
                }
                setGameId(list[0].id);
                setTopics(await ClassSessionService.getTopics(list[0].id));
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
            } finally {
                setCatalogLoading(false);
            }
        })();
    }, []);

    const subjects = useMemo(
        () => [...new Set(topics.map((t) => t.subject))].sort((a, b) => a.localeCompare(b)),
        [topics]
    );

    const filteredTopics = useMemo(
        () => topics.filter((t) => !subject || t.subject === subject),
        [subject, topics]
    );

    const onGameChange = async (id: string) => {
        setGameId(id);
        setSubject('');
        setTopicId('');
        setTopics(await ClassSessionService.getTopics(id));
    };

    const minLocal = useMemo(() => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    }, []);

    const submit = async () => {
        if (!topicId || !startLocal) {
            toast.error('Select a topic and start time.');
            return;
        }
        const startUtc = new Date(startLocal);
        if (Number.isNaN(startUtc.getTime()) || startUtc.getTime() < Date.now() - 5 * 60 * 1000) {
            toast.error('Start time must be in the future.');
            return;
        }
        setPending(true);
        try {
            const created = await ClassSessionService.create({
                organizationId,
                topicId,
                startTimeUtc: startUtc.toISOString(),
            });
            toast.success('Session created.');
            if (created.launchUrl) {
                setCreatedLaunchUrl(created.launchUrl);
            } else {
                await onCreated();
            }
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setPending(false);
        }
    };

    if (createdLaunchUrl) {
        return (
            <Modal onClose={() => void onCreated()} title="Session created">
                <p className="mb2x">
                    Open QuizWhizzer with the launch link, start the game, then paste the access code on the session
                    page.
                </p>
                <div className="disp-flex flex-gap flex-wrap">
                    <SolidButton
                        as="a"
                        href={createdLaunchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                    >
                        Open in QuizWhizzer
                    </SolidButton>
                    <HollowButton as="button" onClick={() => void onCreated()} size="small">
                        Done
                    </HollowButton>
                </div>
            </Modal>
        );
    }

    return (
        <Modal onClose={onClose} title="Create session">
            {catalogLoading && <p>Loading catalog…</p>}
            {!catalogLoading && (
                <>
                    <Select
                        className="mb2x"
                        label="Game"
                        value={gameId}
                        onChange={(e) => void onGameChange(e.target.value)}
                    >
                        {games.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.name}
                            </option>
                        ))}
                    </Select>
                    <Select
                        className="mb2x"
                        label="Subject"
                        value={subject}
                        onChange={(e) => {
                            setSubject(e.target.value);
                            setTopicId('');
                        }}
                    >
                        <option value="">Select subject</option>
                        {subjects.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                    <Select
                        className="mb2x"
                        label="Topic"
                        value={topicId}
                        onChange={(e) => setTopicId(e.target.value)}
                    >
                        <option value="">Select topic</option>
                        {filteredTopics.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.topic}
                            </option>
                        ))}
                    </Select>
                    <TextInput
                        className="mb2x"
                        label="Start time (your local time)"
                        type="datetime-local"
                        min={minLocal}
                        value={startLocal}
                        onChange={(e) => setStartLocal(e.target.value)}
                    />
                    <p className="mb2x text-small">
                        After creating, open the QuizWhizzer launch link, start the game, then paste the access code on
                        the session page.
                    </p>
                    <div className="disp-flex flex-gap">
                        <HollowButton as="button" onClick={onClose} size="small">
                            Cancel
                        </HollowButton>
                        <SolidButton as="button" onClick={submit} pending={pending} size="small">
                            Create
                        </SolidButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default ClassSessionSchedule;
